import { API_BASE_URL } from "../config/api";
import { fetchWithToken } from "../services/api";

/** One member of a mystery slab team, as Manage Teams lists them. */
export type MysteryTeamMember = {
  userId: number;
  name: string | null;
  email: string | null;
  role: string | null;
  enabled: boolean;
};

/**
 * A mystery slab team: the staff of one physical store, who see only their own batches and cards.
 * `members` and `batchCount` come only with the admin list (fetchMysteryTeams).
 */
export type MysteryTeam = {
  id: number;
  name: string;
  members?: MysteryTeamMember[] | null;
  batchCount?: number | null;
};

/** What the sidebar's team selector offers this user in this region. */
export type MysteryTeamAccess = {
  /** False when the region has no teams: Mystery Slabs works as it always has. */
  teamsEnabled: boolean;
  /** An admin, who may also pick "All teams". */
  allTeams: boolean;
  /** An admin, who may create teams and assign staff and batches. */
  canManage: boolean;
  /** Every team for an admin, the user's own otherwise, in name order; the first is the default. */
  teams: MysteryTeam[];
};

export type MysteryTeamRequest = {
  name?: string;
  /** The whole member list (user ids); omit to leave the members as they are. */
  memberIds?: number[];
};

export const fetchMyMysteryTeams = (): Promise<MysteryTeamAccess> =>
  fetchWithToken({
    method: "GET",
    endpoint: `${API_BASE_URL}/mystery-slabs/teams/mine`,
    // An older backend without teams answers 404; the provider then works without teams.
    silent: true,
  });

export const fetchMysteryTeams = (): Promise<MysteryTeam[]> =>
  fetchWithToken({ method: "GET", endpoint: `${API_BASE_URL}/mystery-slabs/teams` });

export const createMysteryTeam = (data: MysteryTeamRequest): Promise<MysteryTeam> =>
  fetchWithToken({ method: "POST", endpoint: `${API_BASE_URL}/mystery-slabs/teams`, payload: data });

export const updateMysteryTeam = (id: number, data: MysteryTeamRequest): Promise<MysteryTeam> =>
  fetchWithToken({ method: "PUT", endpoint: `${API_BASE_URL}/mystery-slabs/teams/${id}`, payload: data });

/** Its batches become unassigned (admins only); no card is deleted. */
export const deleteMysteryTeam = (id: number): Promise<void> =>
  fetchWithToken({ method: "DELETE", endpoint: `${API_BASE_URL}/mystery-slabs/teams/${id}` });

/**
 * Give unassigned batches to a team. A batch belongs to its team for good, so one that already has
 * a team refuses the whole request. All or nothing.
 */
export const assignMysteryBatchesToTeam = (
  batchIds: number[],
  teamId: number
): Promise<{ updated: number }> =>
  fetchWithToken({
    method: "PUT",
    endpoint: `${API_BASE_URL}/mystery-slabs/batches/team`,
    payload: { batchIds, teamId },
  });
